<template>
  <div ref="chartRef" :style="{ width: width, height: height }"></div>
</template>

<script>
import { ref, onMounted, watch, onBeforeUnmount } from 'vue';
import * as echarts from 'echarts';

export default {
  name: 'MultiTrendChart',
  props: {
    title: {
      type: String,
      default: '趨勢圖'
    },
    /** [{ name, color, data: [{ date, value }], yAxisIndex?: 0|1, type?: 'line'|'bar' }] */
    series: {
      type: Array,
      required: true,
      default: () => []
    },
    xAxisKey: {
      type: String,
      default: 'date'
    },
    yAxisKey: {
      type: String,
      default: 'value'
    },
    width: {
      type: String,
      default: '100%'
    },
    height: {
      type: String,
      default: '320px'
    },
    smooth: {
      type: Boolean,
      default: true
    }
  },
  setup(props) {
    const chartRef = ref(null);
    let chartInstance = null;

    // 對齊用 key：YYYY-MM 或 YYYY-MM-DD（ISO 時間以台北日曆解析，避免 UTC 偏一天）
    const toDateKey = (dateString) => {
      if (!dateString) return '';
      if (typeof dateString === 'string' && /^\d{4}-\d{2}$/.test(dateString)) {
        return dateString;
      }
      if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }
      const date = dateString instanceof Date ? dateString : new Date(dateString);
      if (Number.isNaN(date.getTime())) return String(dateString);
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Taipei',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(date);
    };

    // 顯示用：月 YYYY/MM；日／週 MM/DD
    const formatAxisLabel = (key) => {
      if (!key) return '';
      if (/^\d{4}-\d{2}$/.test(key)) return key.replace('-', '/');
      const m = String(key).match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (m) return `${m[2]}/${m[3]}`;
      return key;
    };

    const buildAlignedSeries = () => {
      const dateSet = new Set();
      (props.series || []).forEach((s) => {
        (s.data || []).forEach((item) => {
          if (item[props.xAxisKey] != null) {
            dateSet.add(toDateKey(item[props.xAxisKey]));
          }
        });
      });
      const dates = Array.from(dateSet).filter(Boolean).sort();
      const hasRightAxis = (props.series || []).some((s) => s.yAxisIndex === 1);

      const echartsSeries = (props.series || []).map((s) => {
        const valueMap = {};
        (s.data || []).forEach((item) => {
          valueMap[toDateKey(item[props.xAxisKey])] = item[props.yAxisKey] ?? 0;
        });
        const seriesType = s.type === 'bar' ? 'bar' : 'line';
        const base = {
          name: s.name,
          type: seriesType,
          yAxisIndex: s.yAxisIndex === 1 ? 1 : 0,
          itemStyle: {
            color: s.color || '#4facfe'
          },
          data: dates.map((d) => valueMap[d] ?? 0)
        };
        if (seriesType === 'bar') {
          return {
            ...base,
            barMaxWidth: 36,
            z: 1
          };
        }
        return {
          ...base,
          smooth: props.smooth,
          showSymbol: dates.length <= 12,
          lineStyle: {
            color: s.color || '#4facfe',
            width: 2
          },
          z: 2
        };
      });

      return { dates, echartsSeries, hasRightAxis };
    };

    const getOption = () => {
      const { dates, echartsSeries, hasRightAxis } = buildAlignedSeries();
      const yAxis = hasRightAxis
        ? [
            {
              type: 'value',
              name: '數量',
              position: 'left',
              alignTicks: true
            },
            {
              type: 'value',
              name: '營業額',
              position: 'right',
              alignTicks: true,
              axisLabel: {
                formatter: (val) => {
                  if (val >= 10000) return `${(val / 10000).toFixed(1)}萬`;
                  return String(val);
                }
              }
            }
          ]
        : { type: 'value' };

      return {
        title: {
          text: props.title,
          left: 'center',
          textStyle: {
            fontSize: 16,
            fontWeight: 500,
            color: '#333'
          }
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            label: {
              backgroundColor: '#6a7985'
            }
          }
        },
        legend: {
          top: 32,
          type: 'scroll',
          data: (props.series || []).map((s) => s.name)
        },
        grid: {
          left: '3%',
          right: hasRightAxis ? '6%' : '4%',
          top: 72,
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: (props.series || []).some((s) => s.type === 'bar'),
          data: dates,
          axisLabel: {
            formatter: (value) => formatAxisLabel(value),
            rotate: dates.length > 10 ? 45 : 0,
            hideOverlap: true,
            interval: dates.length > 20 ? 'auto' : 0
          }
        },
        yAxis,
        series: echartsSeries
      };
    };

    const initChart = () => {
      if (!chartRef.value) return;
      chartInstance = echarts.init(chartRef.value);
      chartInstance.setOption(getOption());
    };

    const updateChart = () => {
      if (!chartInstance) return;
      chartInstance.setOption(getOption(), true);
    };

    const resizeChart = () => {
      if (chartInstance) {
        chartInstance.resize();
      }
    };

    onMounted(() => {
      initChart();
      window.addEventListener('resize', resizeChart);
    });

    watch(() => props.series, () => {
      updateChart();
    }, { deep: true });

    watch(() => props.title, () => {
      updateChart();
    });

    onBeforeUnmount(() => {
      if (chartInstance) {
        chartInstance.dispose();
        chartInstance = null;
      }
      window.removeEventListener('resize', resizeChart);
    });

    return {
      chartRef
    };
  }
};
</script>

<style scoped>
</style>
